import React, { useEffect, useMemo, useState } from "react";
import {
  BellRing,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCw,
  Search,
  XCircle,
} from "lucide-react";
import useNotification from "../../Sevices/useNotification";

export default function NotificationStatus() {
  const { getAllNotificationStatus, notificationStatusLoading } =
    useNotification();

  const [notificationStatuses, setNotificationStatuses] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const recordsPerPage = 25;

  const fetchNotificationStatuses = async () => {
    const data = await getAllNotificationStatus();
    setNotificationStatuses(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    fetchNotificationStatuses();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const filteredStatuses = useMemo(() => {
    const value = search.trim().toLowerCase();

    if (!value) {
      return notificationStatuses;
    }

    return notificationStatuses.filter((item) => {
      return (
        String(item?.pk_notification_id || "")
          .toLowerCase()
          .includes(value) ||
        String(item?.driver_code || "")
          .toLowerCase()
          .includes(value) ||
        String(item?.task_id || "")
          .toLowerCase()
          .includes(value) ||
        String(item?.status || "")
          .toLowerCase()
          .includes(value) ||
        String(item?.name || "")
          .toLowerCase()
          .includes(value) ||
        String(item?.created_on || "")
          .toLowerCase()
          .includes(value)
      );
    });
  }, [notificationStatuses, search]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredStatuses.length / recordsPerPage),
  );

  const paginatedStatuses = useMemo(() => {
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;

    return filteredStatuses.slice(startIndex, endIndex);
  }, [filteredStatuses, currentPage]);

  const formatDateTime = (dateValue) => {
    if (!dateValue) return "-";

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return dateValue;
    }

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const getStatusBadge = (status) => {
    const normalizedStatus = String(status || "")
      .trim()
      .toLowerCase();

    const isSuccess =
      normalizedStatus === "yes" ||
      normalizedStatus === "y" ||
      normalizedStatus === "sent" ||
      normalizedStatus === "success";

    if (isSuccess) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
          <CheckCircle2 className="h-3.5 w-3.5" />
          {status || "Yes"}
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
        <XCircle className="h-3.5 w-3.5" />
        {status || "No"}
      </span>
    );
  };

  return (
    <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-col gap-4 border-b border-gray-200 px-5 py-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <BellRing className="h-5 w-5 text-orange-500" />

            <h2 className="text-lg font-bold text-gray-800">
              Notification Logs
            </h2>
          </div>

          <p className="mt-1 text-sm text-gray-500">
            Total {filteredStatuses.length} notification records
          </p>
        </div>

        <div className="flex w-full flex-col gap-3 sm:flex-row md:w-auto">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search driver, task or status..."
              className="w-full rounded-xl border border-gray-200 py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
            />
          </div>

          <button
            type="button"
            onClick={fetchNotificationStatuses}
            disabled={notificationStatusLoading}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {notificationStatusLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </button>
        </div>
      </div>

      <div className="max-h-[calc(100vh-280px)] overflow-auto">
        <table className="min-w-full border-collapse text-left">
          <thead className="sticky top-0 z-10 bg-[#2EC8F1] text-white text-center">
            <tr className="border-b border-gray-200"> 

              <th className="whitespace-nowrap px-4 py-3 text-xs font-bold uppercase tracking-wide">
                Notification ID
              </th>

              <th className="whitespace-nowrap px-4 py-3 text-xs font-bold uppercase tracking-wide">
                Driver
              </th>

              <th className="whitespace-nowrap px-4 py-3 text-xs font-bold uppercase tracking-wide">
                Driver Code
              </th>

              <th className="whitespace-nowrap px-4 py-3 text-xs font-bold uppercase tracking-wide">
                Task ID
              </th>

              <th className="whitespace-nowrap px-4 py-3 text-xs font-bold uppercase tracking-wide">
                Status
              </th>

              <th className="whitespace-nowrap px-4 py-3 text-xs font-bold uppercase tracking-wide">
                Created On
              </th>
            </tr>
          </thead>

          <tbody>
            {notificationStatuses.map((item, index) => (
              <tr
                key={item.pk_notification_id}
                className="border-b hover:bg-gray-50 text-center"
              >
                <td className="p-3">{index + 1}</td>

                <td className="p-3">{item.name || "-"}</td>

                <td className="p-3">{item.driver_code || "-"}</td>

                <td className="p-3">{item.task_id || "-"}</td>

                <td className="p-3">
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                      item.status?.toLowerCase() === "yes"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {item.status || "-"}
                  </span>
                </td>

                <td className="p-3">
                  {item.created_on
                    ? new Date(item.created_on).toLocaleString("en-IN")
                    : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!notificationStatusLoading && filteredStatuses.length > 0 && (
        <div className="flex flex-col gap-3 border-t border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-500">
            Showing{" "}
            <span className="font-semibold text-gray-700">
              {(currentPage - 1) * recordsPerPage + 1}
            </span>{" "}
            to{" "}
            <span className="font-semibold text-gray-700">
              {Math.min(currentPage * recordsPerPage, filteredStatuses.length)}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-gray-700">
              {filteredStatuses.length}
            </span>
          </p>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() =>
                setCurrentPage((previous) => Math.max(previous - 1, 1))
              }
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <span className="rounded-lg bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-600">
              Page {currentPage} of {totalPages}
            </span>

            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() =>
                setCurrentPage((previous) => Math.min(previous + 1, totalPages))
              }
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
